import React, { useState } from 'react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import styles from './HeaderNavbarMobile.module.scss'
import Button from '../common/Button'
import { Link } from 'gatsby'
import { PATH, URL } from '../../constants/content'
import { isLocalLink } from '../../helpers/url'
import { ReactComponent as ArrowSVG } from './ArrowSVG.svg'
import { ReactComponent as ArrowBackSVG } from './ArrowBackSVG.svg'

import UseCasesItem from './UseCasesItem/UseCasesItem'
import APIStatusItem from './APIStatusItem/APIStatusItem'

import {
  // PATH,
  solutionsDropdown,
  platformDropdown,
  industryDropdown,
  devResourcesDropdown,
  resourcesDropdown,
  demoDropdown,
} from '../../constants/content'

interface HeaderNavbarMobileProps {
  darkMode?: boolean
  showRequestApiKey?: boolean
  demoMenuEmbeddedExperiment?: boolean
}

export enum ItemId {
  Platform = 'platform',
  Solutions = 'solutions',
  Developers = 'developers',
  Resources = 'resources',
  Pricing = 'pricing',
  Demo = 'demo',
}

const Platform = [
  {
    title: 'Capabilities',
    list: platformDropdown.integrations,
  },
  {
    title: 'Platform',
    list: platformDropdown.capabilities,
  },
]

const Solutions = [
  { title: 'Protect', list: solutionsDropdown.protect },
  { title: 'Grow', list: solutionsDropdown.grow },
  { title: 'By Industry', list: industryDropdown, isWrapped: true },
]

const Developers = (demoMenuEmbeddedExperiment?: boolean) => [
  { title: 'Dev Resources', list: devResourcesDropdown, centerAligned: true },
  demoMenuEmbeddedExperiment ? { title: 'Demo', list: demoDropdown, isWrapped: true } : null,
]

const Demo = [{ title: 'Demo', list: demoDropdown.slice(0, 2), centerAligned: true }]

const Resources = [{ title: 'Resources', list: resourcesDropdown, centerAligned: true }]

export default function HeaderNavbarMobile({ darkMode, demoMenuEmbeddedExperiment }: HeaderNavbarMobileProps) {
  const [selectedItem, setSelectedItem] = useState<ItemId | null>(null)

  return (
    <nav className={classNames(styles.nav, { [styles.darkNavHeader]: darkMode, [styles.open]: selectedItem !== null })}>
      <div className={styles.container}>
        <AnimatePresence mode='wait'>
          {selectedItem && (
            <motion.div
              className={styles.item}
              initial={{ opacity: 0, x: 250 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 250 }}
              transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div
                onClick={() => setSelectedItem(null)}
                className={classNames(styles.itemHeader, { [styles.darkItemHeader]: darkMode })}
              >
                <ArrowBackSVG />
                <h4>Back</h4>
              </div>

              {selectedItem === ItemId.Platform && <HeaderMenu sections={Platform} darkMode={darkMode} />}
              {selectedItem === ItemId.Solutions && (
                <HeaderMenu
                  sections={Solutions}
                  bottomComponents={[
                    <UseCasesItem
                      key='use-cases'
                      darkMode={darkMode}
                      bottomLinkUrl={PATH.useCases}
                      bottomLinkText='See all use cases'
                    />,
                  ]}
                  darkMode={darkMode}
                />
              )}
              {selectedItem === ItemId.Developers && (
                <HeaderMenu
                  sections={Developers(demoMenuEmbeddedExperiment).filter((section) => section !== null)}
                  bottomComponents={[<APIStatusItem marginTop darkMode={darkMode} key='api-status' />]}
                  darkMode={darkMode}
                />
              )}
              {selectedItem === ItemId.Resources && <HeaderMenu sections={Resources} darkMode={darkMode} />}
              {selectedItem === ItemId.Demo && (
                <HeaderMenu
                  sections={Demo}
                  darkMode={darkMode}
                  bottomComponents={[
                    <DemoBottomLeftComponent
                      url={demoDropdown[2].url}
                      title={demoDropdown[2].title}
                      Icon={demoDropdown[2].icon}
                      darkMode={darkMode}
                      key='demo-bottom-left'
                    />,
                  ]}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {selectedItem === null && (
            <motion.ul
              className={classNames(styles.menu, { [styles.darkMenu]: darkMode })}
              initial={{ opacity: 0, x: -250 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -250 }}
              transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <li onClick={() => setSelectedItem(ItemId.Platform)}>
                <h4>Product</h4>
                <ArrowSVG />
              </li>
              <li onClick={() => setSelectedItem(ItemId.Solutions)}>
                <h4>Use Cases</h4>
                <ArrowSVG />
              </li>
              <li onClick={() => setSelectedItem(ItemId.Developers)}>
                <h4>Developers</h4>
                <ArrowSVG />
              </li>
              <li onClick={() => setSelectedItem(ItemId.Resources)}>
                <h4>Resources</h4>
                <ArrowSVG />
              </li>
              {!demoMenuEmbeddedExperiment && (
                <li onClick={() => setSelectedItem(ItemId.Demo)}>
                  <h4>Demo</h4>
                  <ArrowSVG />
                </li>
              )}
              <Link to={PATH.pricingUrl}>
                <li>
                  <h4>Pricing</h4>
                </li>
              </Link>
              <Link to={PATH.contactSales}>
                <li>
                  <h4>Contact Sales</h4>
                </li>
              </Link>
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
      <div className={classNames(styles.footer, { [styles.darkFooter]: darkMode })}>
        <Button size='small' variant='white' href={URL.signupUrl} className={styles.button}>
          Get Started
        </Button>
      </div>
    </nav>
  )
}

interface MenuItemType {
  title: string
  url: string
  description?: string
  isNew?: boolean
  icon?: React.ComponentType<{ className?: string }>
}

interface MenuSectionType {
  title?: string
  list: MenuItemType[]
  isWrapped?: boolean
  centerAligned?: boolean
}

interface HeaderMenuProps {
  sections: MenuSectionType[]
  bottomComponents?: React.ReactNode[]
  darkMode?: boolean
}

function HeaderMenu({ sections, bottomComponents, darkMode }: HeaderMenuProps) {
  return (
    <div className={classNames(styles.menuContainer, { [styles.darkMenuContainer]: darkMode })}>
      {sections.map((section, sectionIndex) => (
        <div
          key={sectionIndex}
          className={classNames({ [styles.wrapped]: section.isWrapped, [styles.darkWrapped]: darkMode })}
        >
          {section.title && <p className={styles.label}>{section.title}</p>}
          <ul className={styles.menuItems}>
            {section.list.map(({ title, url, description, isNew, icon: Icon }, index) =>
              isLocalLink(url) ? (
                <Link
                  key={`${index}-${url}`}
                  to={url}
                  className={classNames(styles.link, {
                    [styles.newContainer]: isNew,
                    [styles.darkLink]: darkMode,
                    [styles.centerAligned]: section.centerAligned,
                  })}
                >
                  {Icon && (
                    <div className={styles.icon}>
                      <Icon />
                    </div>
                  )}
                  {isNew && <h5 className={styles.newLabel}>What&apos;s new_</h5>}
                  <li className={styles.row}>
                    <h3 className={styles.linkTitle}>{title}</h3>
                    {description && <p className={styles.linkDescription}>{description}</p>}
                  </li>
                </Link>
              ) : (
                <a
                  key={`${index}-${url}`}
                  href={url}
                  className={classNames(styles.link, {
                    [styles.newContainer]: isNew,
                    [styles.darkLink]: darkMode,
                    [styles.centerAligned]: section.centerAligned,
                  })}
                  rel='noreferrer'
                >
                  {Icon && (
                    <div className={styles.icon}>
                      <Icon />
                    </div>
                  )}
                  {isNew && <h5 className={styles.newLabel}>What&apos;s new_</h5>}
                  <li className={styles.row}>
                    <h3 className={styles.linkTitle}>{title}</h3>
                    {description && <p className={styles.linkDescription}>{description}</p>}
                  </li>
                </a>
              )
            )}
          </ul>
        </div>
      ))}
      {bottomComponents && bottomComponents.map((component, index) => <li key={index}>{component}</li>)}
    </div>
  )
}

const DemoBottomLeftComponent = ({
  url,
  title,
  Icon,
  darkMode,
}: {
  url: string
  title: string
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  darkMode?: boolean
}) => {
  return (
    <Link to={url}>
      <div className={classNames(styles.useCaseLink, { [styles.darkUseCaseLink]: darkMode })}>
        <div className={styles.icon}>
          <Icon />
        </div>
        {title}
      </div>
    </Link>
  )
}
